import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Video } from "lucide-react";
import Modal, { Field, Input, Select, GoldButton, GhostButton, PageHeader, EmptyState } from "@/components/Ui";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

export default function AdminCourtVcLinks() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ court: "", category: "", bench: "", hearing_type: "", url: "", active: true, scope: "global" });
    const [saving, setSaving] = useState(false);
    const [editId, setEditId] = useState(null);
    const [opts, setOpts] = useState({ courts: [], categories: [], benches: [], hearings: [] });
    const ctrl = useTableControls(items, {
        storageKey: "admin-court-vc-links",
        initialSort: { key: "court", dir: "asc" },
        accessors: { active: r => (r.active ? "yes" : "no") },
    });

    const load = () => api.get("/platform/court-vc-links").then(r => setItems(r.data)).catch(e => toast.error(apiError(e)));
    useEffect(() => {
        load();
        Promise.all([
            api.get("/platform/config/courts").catch(() => ({ data: [] })),
            api.get("/platform/config/case_categories").catch(() => ({ data: [] })),
            api.get("/platform/config/benches").catch(() => ({ data: [] })),
            api.get("/platform/config/hearing_types").catch(() => ({ data: [] })),
        ]).then(([co, ca, be, he]) => setOpts({
            courts: (co.data || []).map(x => ({ value: x.name, label: x.name })),
            categories: (ca.data || []).map(x => ({ value: x.name, label: x.name })),
            benches: (be.data || []).map(x => ({ value: x.name, label: x.name })),
            hearings: (he.data || []).map(x => ({ value: x.name, label: x.name })),
        }));
    }, []);

    const submit = async (e) => {
        e.preventDefault(); setSaving(true);
        try {
            if (editId) {
                if (!form.reason || form.reason.trim().length < 5) {
                    toast.error("Reason for change (min 5 chars) is mandatory"); setSaving(false); return;
                }
                const { reason, ...patch } = form;
                await api.patch(`/platform/court-vc-links/${editId}`, patch, { headers: { "X-Change-Reason": reason } });
                toast.success("VC link updated");
            } else {
                const { reason, ...rest } = form;
                await api.post("/platform/court-vc-links", rest);
                toast.success("VC link added");
            }
            setOpen(false); setEditId(null);
            setForm({ court: "", category: "", bench: "", hearing_type: "", url: "", active: true, scope: "global", reason: "" });
            load();
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    const openEdit = (it) => {
        setEditId(it.id);
        setForm({
            court: it.court || "",
            category: it.category || "",
            bench: it.bench || "",
            hearing_type: it.hearing_type || "",
            url: it.url || "",
            active: it.active !== false,
            scope: "global",
            reason: "",
        });
        setOpen(true);
    };

    const openAdd = () => {
        setEditId(null);
        setForm({ court: "", category: "", bench: "", hearing_type: "", url: "", active: true, scope: "global", reason: "" });
        setOpen(true);
    };

    const remove = async (id) => {
        const reason = window.prompt("Reason for deleting this global VC link (min 5 chars):");
        if (!reason || reason.trim().length < 5) {
            if (reason !== null) toast.error("Reason is mandatory");
            return;
        }
        try { await api.delete(`/platform/court-vc-links/${id}`, { headers: { "X-Change-Reason": reason.trim() } }); toast.success("Removed"); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    return (
        <>
            <PageHeader
                title="Court VC links"
                subtitle="Master list of video-conference URLs. Firms inherit these; clicking 'Appear Now' on a case picks the closest match (court → category → bench → hearing type)."
                action={<GoldButton onClick={openAdd} data-testid="vc-add-btn"><Plus className="w-4 h-4 inline mr-2" strokeWidth={2}/>Add link</GoldButton>}
            />
            {items.length === 0 ? (
                <EmptyState title="No VC links yet" subtitle="Add the first one to make 'Appear Now' work for all firms."/>
            ) : (
                <>
                <FilterChip ctrl={ctrl} className="mb-3"/>
                <div className="bg-muted border border-border">
                    <table className="w-full text-sm">
                        <thead><tr className="border-b border-border">
                            <SortableTh field="court" ctrl={ctrl} className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Court</SortableTh>
                            <SortableTh field="category" ctrl={ctrl} className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Category</SortableTh>
                            <SortableTh field="bench" ctrl={ctrl} className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Bench</SortableTh>
                            <SortableTh field="hearing_type" ctrl={ctrl} className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Hearing type</SortableTh>
                            <SortableTh field="url" ctrl={ctrl} className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">URL</SortableTh>
                            <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground"></th>
                        </tr></thead>
                        <tbody>
                            {ctrl.rows.map(it => (
                                <tr key={it.id} className="border-b border-background" data-testid={`vc-row-${it.id}`}>
                                    <td className="px-4 py-3">{it.court}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{it.category || "—"}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{it.bench || "—"}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{it.hearing_type || "—"}</td>
                                    <td className="px-4 py-3"><a href={it.url} target="_blank" rel="noreferrer" className="text-primary hover:underline truncate inline-block max-w-[260px] font-mono text-xs">{it.url}</a></td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="inline-flex items-center gap-3">
                                            <button onClick={() => openEdit(it)} data-testid={`vc-edit-${it.id}`} className="text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" strokeWidth={1.5}/></button>
                                            <button onClick={() => remove(it.id)} data-testid={`vc-delete-${it.id}`} className="text-muted-foreground hover:text-red-400"><Trash2 className="w-4 h-4" strokeWidth={1.5}/></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                </>
            )}

            <Modal open={open} onClose={() => { setOpen(false); setEditId(null); }} title={editId ? "Edit court VC link" : "Add court VC link"} size="lg" testId="vc-modal">
                <form onSubmit={submit} className="space-y-4">
                    <Field label="URL"><Input required type="url" value={form.url} onChange={e => setForm({...form, url: e.target.value})} placeholder="https://vc.example.gov.in/..." data-testid="vc-url-input"/></Field>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Court">
                            <Select value={form.court} onChange={e => setForm({...form, court: e.target.value})}
                                options={[{ value: "", label: "— select court —" }, ...opts.courts]} data-testid="vc-court-select"/>
                        </Field>
                        <Field label="Category" hint="Leave blank to match any.">
                            <Select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                                options={[{ value: "", label: "— any —" }, ...opts.categories]} data-testid="vc-category-select"/>
                        </Field>
                        <Field label="Bench" hint="Leave blank to match any.">
                            <Select value={form.bench} onChange={e => setForm({...form, bench: e.target.value})}
                                options={[{ value: "", label: "— any —" }, ...opts.benches]} data-testid="vc-bench-select"/>
                        </Field>
                        <Field label="Hearing type" hint="Leave blank to match any.">
                            <Select value={form.hearing_type} onChange={e => setForm({...form, hearing_type: e.target.value})}
                                options={[{ value: "", label: "— any —" }, ...opts.hearings]} data-testid="vc-hearing-select"/>
                        </Field>
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                        Don't see a value? Add it under <span className="text-primary">Master data</span> first.
                        More-specific entries automatically win when multiple match.
                    </p>
                    {editId && (
                        <Field label="Reason for change *" hint="Audit log requirement (min 5 characters)">
                            <Input required minLength="5" value={form.reason || ""} onChange={e => setForm({...form, reason: e.target.value})}
                                placeholder="e.g. SC e-filing portal moved court rooms" data-testid="vc-reason-input"/>
                        </Field>
                    )}
                    <div className="flex justify-end pt-2"><GoldButton disabled={saving || !form.court} data-testid="vc-save-btn">{saving ? "..." : "Save"}</GoldButton></div>
                </form>
            </Modal>
        </>
    );
}
